import { capitalize } from 'lodash';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { FormField } from '~/eds/src/components/FormField/FormField';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Markdown } from '~/eds/src/components/Markdown/Markdown';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { StatusMessage } from '~/eds/src/components/StatusMessage/StatusMessage';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { ButtonVariant } from '~/eds/src/types/ButtonVariant';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { Nullable } from '~/types/Nullable';

import { useResolveTargetEntityDetails } from '../hooks/useResolveTargetEntityDetails';
import { TargetEntityDetails } from '../types/entity';
import { getEntityDisplayType } from '../utils/entity';
import { getErrorMessage, resolveModalProps } from '../utils/modals';
import { ConfirmResumePublishModal } from './ConfirmResumePublishModal';
import { ConfirmResumePublishQuotaModal } from './ConfirmResumePublishQuotaModal';

export const ModelModal = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { toast } = useToast();

  const enableXrayNewPricingModel = useFlag(
    FlagType.DocumentXrayNewPricingModel,
  );

  const [deleteInput, setDeleteInput] = useState<Nullable<string>>('');

  const [deleteFieldModel, { isLoading: isDeletingModel }] =
    api.endpoints.deletePromptModel.useMutation();

  const [suspendFieldModel, { isLoading: isSuspendingModel }] =
    api.endpoints.suspendPromptModel.useMutation();

  const modelAction = useSelector(slices.xRay.selectors.selectModelAction);

  const isLoading = isDeletingModel || isSuspendingModel;

  const [targetEntityDetails, isResolvingTargetEntity] =
    useResolveTargetEntityDetails(modelAction?.model.entity ?? null);

  const [modalActionProps, setModalActionProps] = useState<{
    description: string;
    toastMessage: string;
    variant: ButtonVariant;
  }>({ description: '', toastMessage: '', variant: 'primary' });

  const [entityDetails, setEntityDetails] =
    useState<Nullable<TargetEntityDetails>>(null);

  useEffect(() => {
    if (isResolvingTargetEntity || !modelAction) {
      return;
    }
    if (modelAction.model.entityDetails) {
      setEntityDetails(modelAction.model.entityDetails);
      if (modelAction.action !== 'resume') {
        const resolvedModalProps = resolveModalProps({
          modalAction: modelAction.action,
          entityDetails: modelAction.model.entityDetails,
          intl,
        });
        setModalActionProps(resolvedModalProps);
      }
    } else if (targetEntityDetails) {
      setEntityDetails(targetEntityDetails);
      if (modelAction.action !== 'resume') {
        const resolvedModalProps = resolveModalProps({
          modalAction: modelAction.action,
          entityDetails: targetEntityDetails,
          intl,
        });
        setModalActionProps(resolvedModalProps);
      }
    }
  }, [targetEntityDetails, modelAction]);

  if (modelAction === null) {
    return null;
  }

  const { model, action } = modelAction;
  const isResumeAction = action === 'resume';
  const isDeleteAction = action === 'delete';
  const hasNewDeleteModal = enableXrayNewPricingModel && isDeleteAction;
  const hasNewResumeModal = enableXrayNewPricingModel && isResumeAction;

  const handleHide = () => {
    if (isDeleteAction) {
      setDeleteInput('');
    }
    dispatch(slices.xRay.actions.setModelAction(null));
  };

  const handleModelActionConfirm = () => {
    if (isResumeAction) return;

    const queries = {
      delete: deleteFieldModel,
      suspend: suspendFieldModel,
    };

    const query = queries[action];

    query({
      modelId: model.id,
      version: model.version,
    })
      ?.unwrap()
      .then(() => {
        toast({
          message: modalActionProps.toastMessage,
          status: 'success',
        });
      })
      .catch(() =>
        toast({
          message: getErrorMessage({
            intl,
            action,
            targetEntityLabel: entityDetails?.label ?? '–',
          }),
          status: 'danger',
        }),
      )
      .finally(handleHide);
  };

  const modalProps = {
    children: hasNewDeleteModal ? (
      <Layout preset="form-fields">
        <Markdown
          text={intl.formatMessage(
            {
              id: 'ModelModal.deleteConfirmationText',
              defaultMessage:
                'Please confirm that you would like to delete {entityType} Model <strong>{targetEntityLabel}</strong>. This will not delete all values previously found by the model, but will remove this model and stop it from running on any new documents that fit within the model scope. <strong>You will not receive</strong> a refund for this model and this action cannot be undone.',
              description:
                'Confirmation text for deleting a model. Example: entityType = "document", targetEntityLabel = "Important"',
            },
            {
              entityType: getEntityDisplayType({
                entityType: entityDetails?.type,
                intl,
              }),
              targetEntityLabel: entityDetails?.label ?? '–',
              strong: (chunks) => `<strong>${chunks}</strong>`,
            },
          )}
        />
        <FormField<string, false>
          input={TextInput}
          label={intl.formatMessage({
            id: 'ModelModal.deleteInputLabel',
            defaultMessage: 'Type Delete to delete model.',
            description: 'Label for delete confirmation input',
          })}
          name={intl.formatMessage({
            id: 'ModelModal.deleteInputName',
            defaultMessage: 'Delete Model',
            description: 'Name for delete confirmation input',
          })}
          onChange={(value) => {
            setDeleteInput(value);
          }}
          value={deleteInput}
        />
      </Layout>
    ) : (
      <Layout preset="form-fields">
        <Markdown text={modalActionProps.description} />{' '}
        {enableXrayNewPricingModel && (
          <StatusMessage
            message={intl.formatMessage({
              id: 'ModelModal.suspendedModelsWarning',
              defaultMessage:
                'Suspended models still count towards your model quota.',
              description:
                'Warning message about suspended models counting towards quota',
            })}
            status="warning"
          />
        )}
      </Layout>
    ),
    disableHideOnEscape: true,
    isVisible: true,
    primaryAction: {
      isLoading,
      text: intl.formatMessage({
        id: 'ModelModal.confirmButton',
        defaultMessage: 'Confirm',
        description: 'Confirm button text',
      }),
      variant: modalActionProps.variant,
      onClick: handleModelActionConfirm,
      disabled: hasNewDeleteModal && deleteInput !== 'Delete',
      tooltip:
        hasNewDeleteModal && deleteInput !== 'Delete'
          ? intl.formatMessage({
              id: 'ModelModal.deleteTooltip',
              defaultMessage: 'Type Delete to delete model.',
              description: 'Tooltip for delete confirmation',
            })
          : undefined,
    },
    title: intl.formatMessage(
      {
        id: 'ModelModal.title',
        defaultMessage: '{capitalizedAction} {entityType} Model',
        description:
          'Modal title for model actions. Example: capitalizedAction = "moved", entityType = "document"',
      },
      {
        capitalizedAction: capitalize(action),
        entityType: getEntityDisplayType({
          entityType: entityDetails?.type,
          intl,
        }),
      },
    ),
    onHide: handleHide,
  };

  return hasNewResumeModal ? (
    <ConfirmResumePublishQuotaModal
      isVisible={true}
      modelId={model.id}
      version={model.version}
      targetEntityDetails={entityDetails}
    />
  ) : isResumeAction ? (
    <ConfirmResumePublishModal
      modelId={model.id}
      version={model.version}
      targetEntityDetails={entityDetails}
    />
  ) : (
    <Modal {...modalProps} />
  );
};
